import { useMutation, useQuery } from '@tanstack/react-query';
import { repository } from './repository';

export const repositoryKeys = {
  clients: ['clients'] as const,
  client: (clientId: string) => ['clients', clientId] as const,
  scenarios: (clientId: string) => ['clients', clientId, 'scenarios'] as const,
  scenario: (clientId: string, scenarioId: string) => ['clients', clientId, 'scenarios', scenarioId] as const,
  exclusions: (clientId: string) => ['clients', clientId, 'exclusions'] as const,
  goals: (clientId: string) => ['clients', clientId, 'goals'] as const,
  followUp: (clientId?: string) => clientId ? ['follow-up', clientId] as const : ['follow-up'] as const,
  financials: (clientId: string) => ['clients', clientId, 'financials'] as const,
  guaranteeExposures: (clientId: string, scenarioId: string | null) => ['clients', clientId, 'guarantee-exposures', scenarioId] as const,
  protection: (clientId: string) => ['clients', clientId, 'protection'] as const,
  pensions: (clientId: string) => ['clients', clientId, 'pensions'] as const,
  financing: (clientId: string) => ['clients', clientId, 'financing'] as const,
  comparisons: (clientId: string) => ['clients', clientId, 'financing-comparisons'] as const,
  family: (clientId: string) => ['clients', clientId, 'family'] as const,
  files: (clientId: string) => ['clients', clientId, 'files'] as const,
  archivedFiles: (clientId: string) => ['clients', clientId, 'archived-files'] as const,
  contributions: (clientId: string) => ['clients', clientId, 'contributions'] as const,
  pipeline: (clientId: string) => ['clients', clientId, 'pipeline'] as const,
  snapshots: ['calculation-snapshots'] as const,
  reports: ['report-snapshots'] as const,
};

export function useClients() {
  return useQuery({ queryKey: repositoryKeys.clients, queryFn: () => repository.listClients() });
}

export function useClient(clientId: string) {
  return useQuery({
    queryKey: repositoryKeys.client(clientId),
    queryFn: () => repository.getClient(clientId),
    enabled: Boolean(clientId),
  });
}

export function useFamilyMembers(clientId: string, enabled = true) {
  return useQuery({ queryKey: repositoryKeys.family(clientId), queryFn: () => repository.listFamilyMembers(clientId), enabled: Boolean(clientId && enabled) });
}

export function useClientFiles(clientId: string, enabled = true) {
  return useQuery({ queryKey: repositoryKeys.files(clientId), queryFn: () => repository.listClientFiles(clientId), enabled: Boolean(clientId && enabled) });
}

export function useArchivedClientFiles(clientId: string) {
  return useQuery({ queryKey: repositoryKeys.archivedFiles(clientId), queryFn: () => repository.listArchivedClientFiles(clientId), enabled: Boolean(clientId) });
}

export function useContributions(clientId: string) {
  return useQuery({ queryKey: repositoryKeys.contributions(clientId), queryFn: () => repository.listContributions(clientId), enabled: Boolean(clientId) });
}

export function useClientPipeline(clientId: string, enabled = true) {
  return useQuery({ queryKey: repositoryKeys.pipeline(clientId), queryFn: () => repository.getClientPipeline(clientId), enabled: Boolean(clientId && enabled) });
}

export function useScenario(clientId: string, scenarioId: string) {
  return useQuery({
    queryKey: repositoryKeys.scenario(clientId, scenarioId),
    queryFn: () => repository.getScenario(clientId, scenarioId),
    enabled: Boolean(clientId && scenarioId),
  });
}

export function useScenarios(clientId: string, enabled = true) {
  return useQuery({
    queryKey: repositoryKeys.scenarios(clientId),
    queryFn: () => repository.listScenarios(clientId),
    enabled: Boolean(clientId && enabled),
  });
}

export function useExclusionSelections(clientId: string) {
  return useQuery({
    queryKey: repositoryKeys.exclusions(clientId),
    queryFn: () => repository.listExclusionSelections(clientId),
    enabled: Boolean(clientId),
  });
}

export function useGoals(clientId: string, enabled = true) {
  return useQuery({
    queryKey: repositoryKeys.goals(clientId),
    queryFn: () => repository.listGoals(clientId),
    enabled: Boolean(clientId && enabled),
  });
}

export function useFollowUp(clientId?: string) {
  return useQuery({
    queryKey: repositoryKeys.followUp(clientId),
    queryFn: () => repository.listFollowUp(clientId),
  });
}

export function useFinancialEntries(clientId: string, enabled = true) {
  return useQuery({
    queryKey: repositoryKeys.financials(clientId),
    queryFn: () => repository.listFinancialEntries(clientId),
    enabled: Boolean(clientId && enabled),
  });
}

export function useGuaranteeExposures(clientId: string, scenarioId: string | null) {
  return useQuery({
    queryKey: repositoryKeys.guaranteeExposures(clientId, scenarioId),
    queryFn: () => repository.listGuaranteeExposures(clientId, scenarioId),
    enabled: Boolean(clientId && scenarioId),
  });
}

export function useProtectionNeed(clientId: string, enabled = true) {
  return useQuery({
    queryKey: repositoryKeys.protection(clientId),
    queryFn: () => repository.getProtectionNeed(clientId),
    enabled: Boolean(clientId && enabled),
  });
}

export function usePrivatePensionSimulations(clientId: string) {
  return useQuery({
    queryKey: repositoryKeys.pensions(clientId),
    queryFn: () => repository.listPrivatePensionSimulations(clientId),
    enabled: Boolean(clientId),
  });
}

export function useFinancingSimulations(clientId: string) {
  return useQuery({
    queryKey: repositoryKeys.financing(clientId),
    queryFn: () => repository.listFinancingSimulations(clientId),
    enabled: Boolean(clientId),
  });
}

export function useFinancingComparisons(clientId: string) {
  return useQuery({
    queryKey: repositoryKeys.comparisons(clientId),
    queryFn: () => repository.listFinancingComparisons(clientId),
    enabled: Boolean(clientId),
  });
}

export function useCalculationSnapshots(clientId?: string) {
  return useQuery({
    queryKey: clientId ? [...repositoryKeys.snapshots, clientId] : repositoryKeys.snapshots,
    queryFn: () => repository.listCalculationSnapshots(clientId),
  });
}

export function useReportSnapshots(clientId?: string) {
  return useQuery({
    queryKey: clientId ? [...repositoryKeys.reports, clientId] : repositoryKeys.reports,
    queryFn: () => repository.listReportSnapshots(clientId),
  });
}

export function useReconcileOperations(clientId: string) {
  return useMutation({
    mutationFn: () => repository.reconcileOperations(clientId),
  });
}

export function useReconcileClientProvisioning(clientId: string) {
  return useMutation({ mutationFn: () => repository.reconcileClientProvisioning(clientId) });
}
